import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Star, ShieldCheck, ExternalLink, MessageSquare } from "lucide-react";

/**
 * BusinessExternalReviewsSection
 * Renders below the native TBC review section on a business profile.
 * Shows the top 5 Google reviews (cached, refreshed weekly) + the TBC
 * Trust Score breakdown chip. Silent no-op if the business isn't
 * linked to a Google Places listing yet.
 */
export default function BusinessExternalReviewsSection({ businessId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/businesses/${businessId}/external-reviews`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [businessId]);

  if (loading || !data) return null;

  const google = data.google;
  const score = data.trust_score;
  const hasGoogle = google && (google.reviews?.length || google.rating);
  const hasTrust = score && score.trust_score != null;

  if (!hasGoogle && !hasTrust) return null;

  return (
    <section
      className="mt-8 space-y-6 bg-tbc-cream/40 rounded-2xl p-6 border border-tbc-gold/20"
      data-testid="external-reviews-section"
    >
      {/* Trust Score breakdown chip */}
      {hasTrust && (
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-2 bg-tbc-navy text-white px-4 py-2 rounded-full">
            <ShieldCheck className="w-4 h-4 text-tbc-gold" />
            <span className="font-heading text-sm">
              TBC Trust Score{" "}
              <strong className="text-tbc-gold text-base">{score.trust_score.toFixed(1)}</strong>
              <span className="text-white/60 text-xs"> /5</span>
            </span>
          </div>
          <div className="flex flex-wrap gap-2 text-xs text-tbc-navy/70 font-body">
            {score.tbc_avg != null && (
              <span className="bg-white px-2.5 py-1 rounded-full border border-tbc-navy/10">
                TBC {score.tbc_avg.toFixed(1)} <span className="text-gray-400">({score.tbc_count})</span>
              </span>
            )}
            {score.google_avg != null && (
              <span className="bg-white px-2.5 py-1 rounded-full border border-tbc-navy/10">
                Google {score.google_avg.toFixed(1)} <span className="text-gray-400">({score.google_count})</span>
              </span>
            )}
            {score.verified_bonus > 0 && (
              <span className="bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-100 text-emerald-700">
                Verified +{score.verified_bonus.toFixed(2)}
              </span>
            )}
            {score.response_bonus > 0 && (
              <span className="bg-blue-50 px-2.5 py-1 rounded-full border border-blue-100 text-blue-700">
                Responds +{score.response_bonus.toFixed(2)}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Google reviews */}
      {hasGoogle && (
        <div>
          <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
            <div>
              <h3 className="font-heading text-xl text-tbc-navy flex items-center gap-2">
                <MessageSquare className="w-4 h-4" /> What Google Reviewers Say
              </h3>
              <div className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                <span className="flex items-center gap-1">
                  <Star className="w-3.5 h-3.5 text-tbc-gold fill-current" />
                  <strong>{google.rating?.toFixed(1)}</strong>
                  <span className="text-gray-400">({google.count} on Google)</span>
                </span>
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  Powered by Google
                </span>
              </div>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {(google.reviews || []).map((r, i) => (
              <article
                key={r.time || `${r.author_name || "anon"}-${i}`}
                className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm"
                data-testid={`google-review-${i}`}
              >
                <header className="flex items-center gap-3 mb-2">
                  {r.author_photo ? (
                    <img
                      src={r.author_photo}
                      alt={r.author_name}
                      className="w-9 h-9 rounded-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-tbc-navy/10 text-tbc-navy flex items-center justify-center text-sm font-semibold">
                      {(r.author_name || "G").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    {r.author_url ? (
                      <a
                        href={r.author_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-tbc-navy hover:text-tbc-gold flex items-center gap-1"
                      >
                        {r.author_name}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <span className="text-sm font-medium text-tbc-navy">{r.author_name}</span>
                    )}
                    <div className="text-xs text-gray-400 flex items-center gap-1">
                      {Array.from({ length: 5 }).map((_, s) => (
                        <Star
                          key={`${r.review_id || r.author_name || "rev"}-star-${s}`}
                          className={`w-3 h-3 ${
                            s < (r.rating || 0)
                              ? "text-tbc-gold fill-current"
                              : "text-gray-300"
                          }`}
                        />
                      ))}
                      {r.relative_time && <span className="ml-1">· {r.relative_time}</span>}
                    </div>
                  </div>
                </header>
                {r.text && (
                  <p className="text-sm text-gray-700 font-body whitespace-pre-wrap">
                    {r.text}
                  </p>
                )}
              </article>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
